import { normalizePortalPath } from '../../../../utilities/normalizePortalPath';
import React from 'react';
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import type { FileRouteTypes } from '@tanstack/react-router';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import { useInitChartSection } from '../../../../hooks/useInitChartSection';
import useChart from '../../../../hooks/useChart';
import { Chart, PatientDoc, FileTypes } from '@usacsmideo/types';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { Uploader } from '../../../../components/Uploader';
import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';
import { isMobile } from 'react-device-detect';
import { Button } from '../../../../components/buttons/Button';
import * as Sentry from '@sentry/browser';
import { Heading } from '../../../../components/Heading';
import Checkbox from '../../../../components/forms/Checkbox';
import { PoaUpload } from '../../../../components/forms/PoaUpload';

import { classNames } from '../../../../style/classNames';
import { updateNavObjAndGetNextScreen } from '../../../../data/navigation/updateNavObjAndGetNextScreen';

export const Route = createFileRoute('/_authenticated/$patientId/info/files')({
  component: FileUploads,
});

function FileUploads() {
  const { patientId } = Route.useParams();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const [organDonorStatus, setOrganDonorStatus] = React.useState('');
  const [hasLivingWill, setHasLivingWill] = React.useState<boolean | undefined>(false);
  const [hasPOLST, setHasPOLST] = React.useState<boolean | undefined>(false);
  const [hasPowerOfAttorney, setHasPowerOfAttorney] = React.useState<boolean | undefined>(false);
  const [noDocs, setNoDocs] = React.useState(false);
  const { mutate, mutateAsync } = useUpdateChartSection();
  const [warned, setWarned] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const { mutate: mutatePatientDoc } = useUpdateUserDoc();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentLocation = routerState.location;
  const pathname = currentLocation.pathname;

  // WARN MOBILE USERS THAT ENABLING CAMERA DISABLES AUDIO ON CONCIERGE CALL
  React.useEffect(() => {
    if (patientInfo && patientInfo.navType?.base === 'concierge' && isMobile && !warned) {
      alert(
        'If you can no longer hear your concierge after using your camera, you may need to return to your Telehealth call and refresh your browser to reconnect with your clinician/concierge',
      );
      setWarned(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warned]);

  const handleNextButtonClick = async () => {
    console.log('Handling next button click');
    const res = await updateNavObjAndGetNextScreen({
      patientId,
      currentPage: pathname,
      patientInfo: patientInfo,
      chart: chart,
    });

    if ('incompleteMessage' in res) {
      // THE PATIENT HAS NOT COMPLETED THE NECESSARY SCREENS
      alert(`Incomplete Information: ${res.incompleteMessage}`);
      return;
    }
    if ('errorMessage' in res) {
      // FIRESTORE ERROR
      alert(`Error: ${res.errorMessage}`);
      return;
    }
    // NAVIGATION COMPLETE — PERSIST FINAL STATE AND NAVIGATE HOME
    if ('complete' in res) {
      mutatePatientDoc(
        { patientId: patientId, navObj: res.updatedNavObj },
        {
          onSuccess: () => {
            navigate({ to: '/' });
          },
          onError: (error: Error) => {
            Sentry.captureException(
              new Error(
                `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
              ),
            );
            console.error('Error updating patient navigation object:', error);
            alert(
              'There was an error updating your information. Please try again later or contact support if this issue persists.',
            );
          },
        },
      );
      return;
    }


    console.log('Updating patient doc with new nav object:', res.updatedNavObj);
    console.log('Next screen to navigate to:', res.nextScreen);
    console.log('Next screen portal path:', res.nextScreen.portalPath);

    mutatePatientDoc(
      {
        patientId: patientId,
        navObj: res.updatedNavObj,
      },
      {
        onSuccess: () => {
          // NAVIGATE TO THE NEXT SCREEN
          navigate({ to: normalizePortalPath(res.nextScreen.portalPath) as FileRouteTypes['to'] });
        },
        onError: (error: Error) => {
          Sentry.captureException(
            new Error(
              `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
            ),
          );
          console.error('Error updating patient navigation object:', error);
          alert(
            'There was an error updating your information. Please try again later or contact support if this issue persists.',
          );
        },
      },
    );
  };

  // HANDLE ORGAN DONOR RADIO BUTTON CLICKS
  const handleOrganDonorStatusChange = (status: string) => {
    setOrganDonorStatus(status);
    mutate(
      {
        patientId: patientId,
        docData: {
          LIVING_WILL: {
            livingWill: hasLivingWill === undefined ? false : hasLivingWill,
            organDonor: status,
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // HANDLE LIVING WILL CHECKBOX CLICKS
  const handleLivingWillStatusChange = () => {
    mutate(
      {
        patientId: patientId,
        docData: {
          OTHER_DOCUMENTS: {
            livingWillExists: !hasLivingWill,
            livingWillURI: '',
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // HANDLE POLST CHECKBOX CLICKS
  const handlePOLSTStatusChange = () => {
    mutate(
      {
        patientId: patientId,
        docData: {
          OTHER_DOCUMENTS: {
            POLSTExists: !hasPOLST,
            POLSTURI: '',
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // HANDLE POWER OF ATTORNEY CHECKBOX CLICKS
  const handlePowerOfAttorneyStatusChange = () => {
    mutate(
      {
        patientId: patientId,
        docData: {
          OTHER_DOCUMENTS: {
            powerOfAttorneyExists: !hasPowerOfAttorney,
            powerOfAttorneyURI: '',
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // SET POWER OF ATTORNEY STATUS FROM FIRESTORE
  React.useEffect(() => {
    if (chart?.LIVING_WILL?.organDonor) {
      setOrganDonorStatus(chart.LIVING_WILL.organDonor);
    }

    setHasLivingWill(chart?.OTHER_DOCUMENTS?.livingWillExists === true ? true : false);
    setHasPOLST(chart?.OTHER_DOCUMENTS?.POLSTExists === true ? true : false);
    setHasPowerOfAttorney(
      chart?.HEALTHCARE_AGENT_INFO.hasPowerOfAttorney === 'yes'
        ? true
        : chart?.OTHER_DOCUMENTS?.powerOfAttorneyExists === true
          ? true
          : chart?.OTHER_DOCUMENTS?.powerOfAttorneyURI && chart.OTHER_DOCUMENTS.powerOfAttorneyURI.length > 0
            ? true
            : false,
    );
  }, [chart]);

  // INITIALIZE FIRESTORE DOCS IF THEY DO NOT EXIST — ONCE PER FAILURE, NOT ONCE PER SNAPSHOT
  useInitChartSection({
    needed: !!chart && !chart.LIVING_WILL,
    write: () =>
      mutateAsync({
        patientId: patientId,
        docData: {
          LIVING_WILL: {
            livingWill: hasLivingWill === undefined ? false : hasLivingWill,
            organDonor: organDonorStatus,
          },
        },
      }),
  });
  useInitChartSection({
    needed: !!chart && !chart.OTHER_DOCUMENTS,
    write: () => mutateAsync({ patientId: patientId, docData: { OTHER_DOCUMENTS: {} } }),
  });

  React.useEffect(() => {
    if (chart?.LIVING_WILL?.organDonor) {
      setOrganDonorStatus(chart.LIVING_WILL.organDonor);
    }
  }, [chart]);

  // // MARK PREVIOUS PAGE AS COMPLETED
  // React.useEffect(() => {
  //   if (patientInfo && patientInfo.navObj) {
  //     markPreviousPageCompleted({
  //       currentPageName: 'files',
  //       patientInfo,
  //       value: true,
  //     });
  //   }
  // }, [patientInfo]);

  // // GET NEXT PAGE URL
  // React.useEffect(() => {
  //   if (chart && patientInfo.navObj) {
  //     (async () => {
  //       const awaitedUrl = await getNextPage({
  //         currentPage: 'files',
  //         chart: chart,
  //         patientInfo: patientInfo,
  //       });
  //       setNextPageURL(awaitedUrl);
  //     })();
  //   }
  // }, [chart, patientInfo]);

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId && chart?.PATIENT_INFO_1) {
    return (
      <>
        <div id="directives-upload-page" className="flex w-full flex-col items-center justify-center">
          <Heading id="directives-heading" textContent="Legal Documents" />
          <div className="w-8/12">
            <details className="mb-1 w-full border-2 border-stone-500 bg-slate-200 p-4 open:bg-slate-50 open:[&_svg]:-rotate-180">
              <summary className="flex cursor-pointer list-none items-center gap-4 text-xl">
                <div className="">
                  <svg
                    className="rotate-0 transform text-blue-700 transition-all duration-300"
                    fill="none"
                    height="20"
                    width="20"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
                <div>Why should I upload my legal documents?</div>
              </summary>
              <p className="text-xl font-bold">Every MIDEO card is printed with two QR codes.</p>
              <p className="text-xl">
                - The QR code on the front is for Emergency personnel and links to the MIDEO safety statement you will
                record today.
              </p>
              <p className="text-xl">
                - The QR code on the reverse links to a paper record of your MIDEO. Uploading documents makes them part
                of this record and therefore instantly available wherever and whenever they are needed.
              </p>
              <p className="text-xl">
                * If you have documents but are not prepared to upload them now, you may check the appropriate box(es)
                and continue with the application. You (or your representative) may then email them to{' '}
                <button
                  className="text-blue-500 underline"
                  onClick={() => (window.location.href = 'mailto:info@mideohealth.com')}
                >
                  info@mideohealth.com
                </button>{' '}
                any time after the application is submitted.
              </p>
              <p className="text-xl">
                **Additional documents (living will and/or medical power of attorney) are not required, but it is
                encouraged that you include them if you have them or are in the process of creating them already.{' '}
              </p>
              {/* <p className="text-lg italic">
                CAUTION: The front of your card will indicate the existence of these documents. You should carry them on
                your person until the time at which they are uploaded and part of your record. Failing to do so, will
                likely cause confusion for those involved in your care
              </p> */}
            </details>
          </div>
          <form>
            {patientInfo.navType?.base !== 'self' && (
              <fieldset>
                <legend className="text-2xl font-bold">Decision Regarding Organ Donation:</legend>
                <div>
                  <div className="ml-4 text-xl">
                    <input
                      type="radio"
                      id="organDonor"
                      name="organDonorStatus"
                      value="Yes"
                      checked={organDonorStatus === 'Yes'}
                      onChange={(e) => handleOrganDonorStatusChange(e.target.value)}
                    />
                    <label className="ml-2 text-xl" htmlFor="organDonor">
                      I am an organ donor
                    </label>
                  </div>

                  <div className="ml-4 text-xl">
                    <input
                      type="radio"
                      id="notOrganDonor"
                      name="organDonorStatus"
                      value="No"
                      checked={organDonorStatus === 'No'}
                      onChange={(e) => handleOrganDonorStatusChange(e.target.value)}
                    />
                    <label className="ml-2" htmlFor="notOrganDonor">
                      I am not an organ donor
                    </label>
                  </div>

                  <div className="ml-4 text-xl">
                    <input
                      type="radio"
                      id="TBD"
                      name="organDonorStatus"
                      value="TBD"
                      checked={organDonorStatus === 'TBD'}
                      onChange={(e) => handleOrganDonorStatusChange(e.target.value)}
                    />
                    <label className="ml-2 text-xl" htmlFor="TBD">
                      My organ donation status is undecided
                    </label>
                  </div>
                </div>
              </fieldset>
            )}
            {/* <h2 className="mt-2 text-2xl font-bold">Legal Documents:</h2> */}
            <div className="mt-6">
              <Checkbox
                id="livingWill"
                name="livingWill"
                checked={hasLivingWill === undefined ? false : hasLivingWill}
                onChange={handleLivingWillStatusChange}
                label="I have a living will"
              />
            </div>
            {hasLivingWill ? (
              chart?.OTHER_DOCUMENTS?.livingWillURI?.startsWith('https://') ? (
                <>
                  <a
                    className="text-xl font-medium text-blue-600 hover:underline dark:text-blue-500"
                    href={chart?.OTHER_DOCUMENTS?.livingWillURI}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <p className="text-xl">View current living will</p>
                  </a>
                </>
              ) : (
                <>
                  <p className="text-xl">Upload a living will:</p>
                  <Uploader
                    docToUpdate="OTHER_DOCUMENTS"
                    bucket="patient"
                    type={FileTypes.livingWill}
                    title="Upload Living Will"
                    elementId="livingWill-file-upload"
                  />
                </>
              )
            ) : null}
            {patientInfo.navType?.base !== 'self' && (
              <div>
                <Checkbox
                  id="hasPOLST"
                  name="POLST"
                  checked={hasPOLST === undefined ? false : hasPOLST}
                  onChange={handlePOLSTStatusChange}
                  label="I have a POLST"
                />
                {hasPOLST ? (
                  chart?.OTHER_DOCUMENTS?.POLSTURI?.startsWith('https://') ? (
                    <>
                      <a
                        className="font-medium text-blue-600 hover:underline dark:text-blue-500"
                        href={chart?.OTHER_DOCUMENTS?.POLSTURI}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <p className="text-xl">View current POLST</p>
                      </a>
                    </>
                  ) : (
                    <>
                      <p className="text-xl">Upload a POLST:</p>
                      <Uploader
                        docToUpdate="OTHER_DOCUMENTS"
                        bucket="patient"
                        type={FileTypes.POLST}
                        title="Upload POLST"
                        elementId="POLST-file-upload"
                      />
                    </>
                  )
                ) : null}
              </div>
            )}
            <div>
              <Checkbox
                id="hasPowerOfAttorney"
                name="powerOfAttorney"
                checked={hasPowerOfAttorney ? hasPowerOfAttorney : false}
                onChange={handlePowerOfAttorneyStatusChange}
                label="I have a medical durable power of attorney"
              />
              {hasPowerOfAttorney ? <PoaUpload patientId={patientId} chart={chart} bucket="patient" /> : null}
            </div>
            {hasLivingWill === false && hasPOLST === false && hasPowerOfAttorney === false && (
              <div>
                <Checkbox
                  id="noDocs"
                  name="noDocs"
                  checked={noDocs}
                  onChange={() => setNoDocs(!noDocs)}
                  label="I don’t have any of the above documents"
                />
              </div>
            )}
          </form>

          <div id="nav" className="flex flex-col items-center">
            <Button
              id="submit-info"
              textContent="Submit"
              type="button"
              onClick={() => setIsSubmitted(true)}
              submitted={isSubmitted}
            />
            {isSubmitted && (
              // <NavForward to={nextPageURL} disabled={organDonorStatus.length === 0} />
              <div className="flex flex-col items-center">
                <button
                  id="submit-form-info"
                  title="Submit Form Info"
                  onClick={handleNextButtonClick}
                  disabled={patientInfo.navType?.base !== 'self' && organDonorStatus.length === 0}
                  className={classNames.nextButton}
                >
                  Next
                </button>
              </div>
            )}
            {/* {patientInfo?.conciergeNavigator?.educationUnlocked && (
              <div className="place-self-center">
                {nextPageURL && organDonorStatus?.length > 0 && isSubmitted && <NextSection to={nextPageURL} />}
              </div>
            )} */}
          </div>
        </div>
      </>
    );
  }
  return <div>Loading...</div>;
}
